import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { CalendarDays, ExternalLink, Gift, Quote, X, FileText } from "lucide-react";
import { resourceIconComp } from "@/lib/resourceIcons";
import { api, resolveImg } from "@/lib/api";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { RichText } from "@/components/site/RichText";
import { EventCard } from "@/components/site/EventCard";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";
import { ShareButton } from "@/components/site/ShareButton";

export default function HolidayPage() {
  const { id } = useParams();
  const { t, tl, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);
  const [resModal, setResModal] = useState(null);

  const { data: holiday, isLoading } = useQuery({
    queryKey: ["holiday", id || "upcoming"],
    queryFn: async () => (await api.get(id ? `/holidays/${id}` : "/holidays/upcoming")).data,
    retry: false,
  });
  const { data: holidays = [] } = useQuery({
    queryKey: ["holidays"],
    queryFn: async () => (await api.get("/holidays")).data,
  });
  const { data: events = [] } = useQuery({
    queryKey: ["events", "upcoming"],
    queryFn: async () => (await api.get("/events?upcoming=true")).data,
  });

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] text-[#475569]"><EditableText k="eventdetail.loading" as="span" /></div>;
  }
  if (!holiday) {
    return (
      <div className="relative bg-[#F9F8F6] min-h-screen" data-testid="holiday-page">
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center gap-4">
          <p className="font-serif text-2xl"><EditableText k="holidaypage.none" as="span" /></p>
          <Link to="/" className="rounded-full bg-[#0F172A] text-white px-6 py-3 text-sm font-semibold"><EditableText k="holidaypage.backhome" as="span" /></Link>
        </div>
      </div>
    );
  }

  const holidayEvents = events.filter((e) => e.holiday === holiday.name);
  const otherHolidays = holidays.filter((h) => h.id !== holiday.id);
  const prettyDate = new Date(holiday.date + "T00:00:00").toLocaleDateString(lang === "he" ? "he-IL" : "en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  // "More holidays" is hidden by default so visitors focus on the current holiday.
  // Exception: during the High Holiday season, each page links to the remaining holidays
  // that follow it in this chain: Rosh Hashanah → Yom Kippur → Sukkot → Simchat Torah.
  // Once Simchat Torah passes, the next holiday (e.g. Chanukah) shows normally with no follow-ups.
  const nameBlob = (h) => [h?.name, ...Object.values(h?.i18n || {}).map((v) => v?.name)].filter(Boolean).join(" ").toLowerCase();
  const highHolidayChain = [
    [/rosh\s*ha?shan/, /ראש השנה/],
    [/kipp?ur/, /כיפור/],
    [/sukk?o[ts]/, /סוכות/],
    [/simcha?[st]\s*torah/, /שמחת תורה/],
  ];
  const chainIndex = (h) => highHolidayChain.findIndex((pats) => pats.some((p) => p.test(nameBlob(h))));
  const currentIndex = chainIndex(holiday);
  const followUpIndexes = currentIndex >= 0
    ? highHolidayChain.map((_, idx) => idx).filter((idx) => idx > currentIndex)
    : [];
  const shownHolidays = followUpIndexes
    .map((idx) => otherHolidays.find((h) => chainIndex(h) === idx))
    .filter(Boolean);

  const resourceHref = (url) => {
    if (!url) return "/#contact";
    if (url.startsWith("#")) return `/${url}`;
    if (url.startsWith("/")) return url;
    // External link: ensure it has a protocol so it isn't treated as relative
    if (/^https?:\/\//i.test(url) || url.startsWith("mailto:") || url.startsWith("tel:")) return url;
    return `https://${url}`;
  };
  const isInternal = (url) => !url || url.startsWith("#") || url.startsWith("/");

  return (
    <div className="relative bg-[#F9F8F6] min-h-screen overflow-x-hidden" data-testid="holiday-page">
      <div className="grain" />
      <Navbar />

      {/* Hero */}
      <section ref={ref} className="relative pt-24 sm:pt-28">
        <div className="relative h-[56vh] min-h-[420px] overflow-hidden">
          <motion.img style={{ y: imgY }} src={resolveImg(holiday.image_url)} alt={tl(holiday, "name")} className="absolute inset-0 w-full h-[120%] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/45 to-[#0F172A]/10" />
          <div className="absolute inset-0 flex items-end">
            <div className="mx-auto max-w-7xl w-full px-5 sm:px-8 pb-12">
              <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="holiday.eyebrow" as="span" /></span>
              <h1 className="mt-3 font-serif text-5xl sm:text-7xl tracking-tight text-white leading-[0.95]">{tl(holiday, "name")}</h1>
              {tl(holiday, "subtitle") && <p className="mt-2 text-lg text-white/70 italic">{tl(holiday, "subtitle")}</p>}
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <p className="inline-flex items-center gap-2 text-white/80"><CalendarDays size={17} className="text-[#BE923C]" /> {prettyDate}</p>
                <ShareButton path={`holidays/${holiday.id}`} title={tl(holiday, "name")} light testid="holiday-share-btn" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info */}
      {tl(holiday, "description") && (
        <section className="relative z-10 py-16 sm:py-20">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            <Reveal>
              <RichText html={tl(holiday, "description")} className="text-[#0F172A] text-lg sm:text-xl leading-relaxed" />
            </Reveal>
          </div>
        </section>
      )}

      {/* Events */}
      <section className="relative z-10 py-16 sm:py-20 bg-[#E8E6E1]/50 border-y border-[#0F172A]/5">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="events.eyebrow" as="span" /></span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight mb-8">{t("holidaypage.events_title").replace("{name}", tl(holiday, "name"))}</h2>
          </Reveal>
          {holidayEvents.length === 0 ? (
            <p data-testid="holiday-events-empty" className="text-[#475569]"><EditableText k="holidaypage.events_empty" as="span" /></p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {holidayEvents.map((ev, i) => (
                <Reveal key={ev.id} delay={i * 0.05}><EventCard event={ev} index={i} /></Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Resources */}
      {holiday.resources?.length > 0 && (
        <section className="relative z-10 py-16 sm:py-20" data-testid="holiday-resources">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal>
              <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="holidaypage.get_ready" as="span" /></span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight mb-8">{t("holidaypage.resources_for")} {tl(holiday, "name")}</h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-4">
              {holiday.resources.map((r, i) => {
                const rt = holiday.i18n?.[lang]?.resources?.[i] || {};
                const rLabel = rt.label || r.label;
                const rDesc = rt.description || r.description;
                const rBody = rt.body || r.body;
                const RIcon = resourceIconComp(r);
                const rtype = r.type || "link";
                const inner = (
                  <>
                    <span className="w-11 h-11 rounded-xl bg-[#BE923C]/10 flex items-center justify-center text-[#BE923C] shrink-0"><RIcon size={20} /></span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-serif text-xl text-start">{rLabel}</h3>
                        {rtype === "flyer" ? <FileText size={14} className="text-[#475569] opacity-0 group-hover:opacity-100 transition-opacity" />
                          : <ExternalLink size={14} className="text-[#475569] opacity-0 group-hover:opacity-100 transition-opacity" />}
                      </div>
                      {rDesc && <p className="mt-1 text-sm text-[#475569] leading-relaxed text-start">{rDesc}</p>}
                    </div>
                  </>
                );
                const cardCls = "group flex items-start gap-4 h-full w-full text-start rounded-2xl bg-white border border-[#0F172A]/5 p-6 hover:border-[#BE923C] hover:shadow-[0_8px_30px_rgba(15,23,42,0.06)] transition-all";
                return (
                <Reveal key={i} delay={(i % 2) * 0.06}>
                  {rtype === "flyer" || rtype === "text" ? (
                    <button type="button" data-testid={`holiday-resource-${i}`} className={cardCls}
                      onClick={() => setResModal({ type: rtype, label: rLabel, flyer_url: (r.flyer_i18n || {})[lang] || r.flyer_url, body: rBody })}>
                      {inner}
                    </button>
                  ) : (
                    <a data-testid={`holiday-resource-${i}`} className={cardCls}
                      href={resourceHref(r.url)}
                      target={isInternal(r.url) ? undefined : "_blank"}
                      rel={isInternal(r.url) ? undefined : "noopener noreferrer"}>
                      {inner}
                    </a>
                  )}
                </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* A word from the Rabbi */}
      {holiday.rabbi_message && (
        <section className="relative z-10 py-16 sm:py-24 bg-[#0F172A] text-white" data-testid="holiday-rabbi">
          <div className="mx-auto max-w-5xl px-5 sm:px-8 grid md:grid-cols-5 gap-10 items-center">
            <Reveal className="md:col-span-2">
              <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.35)] max-w-xs">
                {holiday.rabbi_photo_url ? (
                  <img src={resolveImg(holiday.rabbi_photo_url)} alt={holiday.rabbi_name} className="w-full object-cover" />
                ) : (
                  <div className="aspect-square bg-[#BE923C]/20" />
                )}
              </div>
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-3">
              <Quote size={36} className="text-[#BE923C]" />
              <p className="mt-4 font-serif text-xl sm:text-2xl leading-relaxed text-white/90 whitespace-pre-line">{tl(holiday, "rabbi_message")}</p>
              {holiday.rabbi_name && <p className="mt-6 text-sm font-semibold text-[#BE923C]">— {holiday.rabbi_name}</p>}
            </Reveal>
          </div>
        </section>
      )}

      {/* Other holidays — hidden by default; shown only on Rosh Hashanah with its follow-up holidays */}
      {shownHolidays.length > 0 && (
        <section className="relative z-10 py-14">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <h3 className="font-serif text-2xl mb-5"><EditableText k="holidaypage.more" as="span" /></h3>
            <div className="flex flex-wrap gap-3">
              {shownHolidays.map((h) => (
                <Link key={h.id} to={`/holidays/${h.id}`} data-testid={`other-holiday-${h.id}`}
                  className="rounded-full border border-[#0F172A]/10 bg-white px-5 py-2.5 text-sm font-semibold hover:border-[#BE923C] transition-colors">
                  {tl(h, "name")}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />

      {resModal && (
        <div data-testid="resource-modal" className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4" onClick={() => setResModal(null)}>
          <div className="relative w-full max-w-3xl max-h-[90vh] overflow-auto rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between gap-4 border-b border-[#0F172A]/10 bg-white/95 backdrop-blur px-5 py-3">
              <h3 className="font-serif text-lg text-[#0F172A]">{resModal.label}</h3>
              <button data-testid="resource-modal-close" onClick={() => setResModal(null)} className="grid place-items-center w-9 h-9 rounded-full hover:bg-[#0F172A]/5 text-[#475569]"><X size={18} /></button>
            </div>
            <div className="p-5">
              {resModal.type === "flyer" && resModal.flyer_url && (
                (resModal.flyer_url.toLowerCase().endsWith(".pdf") ? (
                  <iframe title={resModal.label} src={resolveImg(resModal.flyer_url)} className="w-full h-[75vh] rounded-lg border border-[#0F172A]/10" />
                ) : (
                  <img src={resolveImg(resModal.flyer_url)} alt={resModal.label} className="mx-auto max-h-[75vh] w-auto rounded-lg" />
                ))
              )}
              {resModal.type === "flyer" && resModal.flyer_url && (
                <div className="mt-4 text-center">
                  <a href={resolveImg(resModal.flyer_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#BE923C] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#a67e31]">Open / download</a>
                </div>
              )}
              {resModal.type === "text" && (
                <div className="prose prose-lg max-w-none text-[#334155]" dangerouslySetInnerHTML={{ __html: resModal.body || "" }} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
